import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  PageHeader, DataTable, Tag, Dialog, ConfirmDialog, Button, Field, TextInput, Select, ComboBox, FileDrop, Banner, SimpleTable,
  Spinner, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { toHebrewNumeral } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import { TRACTATE_NAMES, getMaxDaf } from '../../data/gemaraTractates.js';
import { useQuickCreate } from '../../next/quickCreate';
import { FormGrid, Row, apiError } from '../../next/ui';
import {
  ALLOWED_FRAMES, FRAME_LABELS, SECTION_LABELS, SECTION_ORDER, IVR_SLOT_LABELS,
  emptyForm, buildDafOptions, clampDaf, formatCountsSummary, formatMaterial, truncate, questionTotal, buildIvrSlotMap, formatCreatedAt,
} from './AmericanExams.jsx';

/**
 * מבחנים אמריקאים — new design. Same endpoints and rules as AmericanExams: list per frame (phone slot 1/2),
 * create (material range + Excel with live preview, rolled back if the import fails), edit details, replace Excel,
 * activate / deactivate, delete and open the question editor.
 */
const TRACTATE_OPTS = TRACTATE_NAMES.map((t) => ({ value: t, label: t }));
const dafOpt = (n) => (n == null ? null : { value: n, label: `${toHebrewNumeral(n)} · ${n}` });
const STATUS_TONES = { active: 'success', scheduled: 'info', inactive: 'neutral' };
/** Date → YYYY-MM-DD לשדה תאריך (שעון מקומי) */
const toDateInput = (d) => {
  if (!d) return '';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};
/** YYYY-MM-DD → תחילת היום בשעון מקומי (ISO) */
const dateInputToIso = (v) => (v ? new Date(`${v}T00:00:00`).toISOString() : null);
const formatOpensAt = (d) => (d ? new Date(d).toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric', year: '2-digit' }) : '');
const statusLabel = (r) => {
  if (r.status === 'active') return 'פעיל';
  if (r.status === 'scheduled') return `עתידי · ${formatOpensAt(r.opensAt)}`;
  return 'כבוי';
};
const AMUD_OPTIONS = [{ value: 'a', label: '. (עמוד א)' }, { value: 'b', label: ': (עמוד ב)' }];
const previewApiError = (err) => {
  const payload = err.response?.data;
  const detail = Array.isArray(payload?.errors) ? payload.errors.slice(0, 4).join(' · ') : '';
  return detail ? `${payload?.error || 'שגיאה'}: ${detail}` : (payload?.error || 'שגיאה בתצוגה מקדימה של הקובץ');
};

function ExcelPreview({ preview }) {
  const rows = useMemo(() => [...(preview?.questions || [])].sort((a, b) => {
    const sa = SECTION_ORDER.indexOf(a.sectionKey);
    const sb = SECTION_ORDER.indexOf(b.sectionKey);
    return sa !== sb ? sa - sb : (a.number || 0) - (b.number || 0);
  }).map((q, i) => ({ ...q, _k: i })), [preview]);
  if (!preview?.questions?.length) return null;
  const summary = formatCountsSummary(preview.questions);
  return (
    <div className="nx-stack" style={{ gap: 6 }}>
      <small className="ck-muted">{`תצוגה מקדימה · ${preview.imported} שאלות${summary ? ` · ${summary}` : ''}`}</small>
      <SimpleTable
        rowKey="_k"
        rows={rows}
        maxHeight={320}
        empty="אין שאלות"
        columns={[
          { key: 'number', label: '#' },
          { key: 'sectionKey', label: 'סוג', render: (q) => SECTION_LABELS[q.sectionKey] || q.sectionKey },
          { key: 'text', label: 'שאלה', render: (q) => <span title={q.text || ''}>{truncate(q.text, 60)}</span> },
          { key: 'correctOption', label: 'נכונה', render: (q) => <Tag tone="success">{q.correctOption}</Tag> },
          { key: 'options', label: 'אפשרויות', render: (q) => <small className="ck-muted">{(q.options || []).map((o, i) => <span key={i} style={{ display: 'block' }}>{`${i + 1}. ${truncate(o)}`}</span>)}</small> },
        ]}
      />
    </div>
  );
}

export default function AmericanExamsNext() {
  const toast = useCkToast();
  const { frame: frameParam } = useParams();
  const frame = String(frameParam || '').trim();
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const [replaceError, setReplaceError] = useState('');
  const [excelPreview, setExcelPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const previewReq = useRef(0);

  const goToEditor = useCallback((id) => { if (id && frame) navigate(`/american-exams/${frame}/${id}`); }, [navigate, frame]);
  const load = useCallback(async () => {
    if (!ALLOWED_FRAMES.has(frame)) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/american-exams', { params: { frame } });
      setExams(data.data || []);
    } catch {
      setError('שגיאה בטעינת מבחנים');
    } finally {
      setLoading(false);
    }
  }, [frame]);
  useEffect(() => { load(); }, [load]);

  const clearPreview = () => { previewReq.current += 1; setExcelPreview(null); setPreviewLoading(false); setPreviewError(''); };
  const previewExcel = async (file) => {
    if (!file) { clearPreview(); return; }
    const req = ++previewReq.current;
    setPreviewLoading(true);
    setPreviewError('');
    setExcelPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/american-exams/import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (req !== previewReq.current) return;
      setExcelPreview({ questions: data.questions || [], counts: data.counts || {}, imported: data.imported || 0 });
    } catch (err) {
      if (req !== previewReq.current) return;
      setPreviewError(previewApiError(err));
    } finally {
      if (req === previewReq.current) setPreviewLoading(false);
    }
  };
  const importFile = async (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post(`/american-exams/${id}/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  };
  const downloadWord = async (exam) => {
    if (!exam.questions?.length) {
      toast.error('אין שאלות במבחן');
      return;
    }
    try {
      const { data } = await api.get(`/american-exams/${exam._id}/docx`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(exam.title || 'מבחן').replace(/[/\\?%*:|"<>]/g, '-')}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('שגיאה בהורדת קובץ הוורד');
    }
  };

  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const { data } = await api.get('/american-exams/template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_american_exam.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('שגיאה בהורדת התבנית');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  /* ── form ── */
  const maxFrom = form.tractate ? getMaxDaf(form.tractate) : null;
  const tractateTo = form.tractateTo || form.tractate;
  const maxTo = tractateTo ? getMaxDaf(tractateTo) : null;
  const dafFromOpts = useMemo(() => buildDafOptions(maxFrom).map(dafOpt), [maxFrom]);
  const dafToOpts = useMemo(() => buildDafOptions(maxTo).map(dafOpt), [maxTo]);
  const setTractateFrom = (v) => setForm((f) => {
    const syncTo = !f.tractateTo || f.tractateTo === f.tractate;
    const max = getMaxDaf(v);
    const next = { ...f, tractate: v, dafFrom: clampDaf(f.dafFrom, max) };
    if (syncTo) { next.tractateTo = v; next.dafTo = clampDaf(f.dafTo, max); }
    return next;
  });
  const setTractateTo = (v) => setForm((f) => ({ ...f, tractateTo: v, dafTo: clampDaf(f.dafTo, getMaxDaf(v)) }));

  const resetDialog = () => { setDialogOpen(false); setEditingId(null); setForm(emptyForm()); setFormError(''); clearPreview(); };
  const openCreate = useCallback(() => { setEditingId(null); setForm({ ...emptyForm(), frame, opensAt: toDateInput(new Date()), opensAtInitial: '' }); setFormError(''); clearPreview(); setDialogOpen(true); }, [frame]);
  useQuickCreate(openCreate);
  const openEdit = (exam) => {
    const m = exam.material || {};
    setEditingId(exam._id);
    setForm({
      title: exam.title || '', frame: exam.frame || 'daf_yomi', description: exam.description || '',
      tractate: m.tractate || null, tractateTo: m.tractateTo || m.tractate || null, dafFrom: m.dafFrom ?? null, dafTo: m.dafTo ?? null,
      amudFrom: m.amudFrom || 'a', amudTo: m.amudTo || 'b', file: null,
      opensAt: toDateInput(exam.opensAt), opensAtInitial: toDateInput(exam.opensAt),
    });
    setFormError('');
    clearPreview();
    setDialogOpen(true);
  };
  const validate = (requireFile) => {
    if (!form.frame) return 'יש לבחור סוג מבחן';
    if (!form.tractate) return 'יש לבחור מסכת התחלה';
    if (!(form.tractateTo || form.tractate)) return 'יש לבחור מסכת סיום';
    const dafFrom = Number(form.dafFrom);
    const dafTo = Number(form.dafTo);
    if (!Number.isInteger(dafFrom) || !Number.isInteger(dafTo) || dafFrom < 2 || dafTo < 2) return 'יש לבחור דף מ ודף עד מהרשימה';
    if (form.frame === 'shinun' && (!form.amudFrom || !form.amudTo)) return 'יש לבחור עמוד התחלה ועמוד סיום';
    if (requireFile && !form.file) return 'יש להעלות קובץ אקסל';
    if (!form.opensAt && (requireFile || form.opensAtInitial)) return 'יש לבחור תאריך פתיחה';
    return '';
  };
  const save = async () => {
    setFormError('');
    const isEdit = !!editingId;
    const err = validate(!isEdit);
    if (err) { setFormError(err); return; }
    if (!isEdit && !excelPreview?.questions?.length) { setFormError(previewError || 'יש לבחור קובץ אקסל תקין'); return; }
    setSaving(true);
    let createdId = null;
    try {
      const to = form.tractateTo || form.tractate;
      const material = {
        tractate: form.tractate,
        tractateTo: to === form.tractate ? '' : to,
        dafFrom: Number(form.dafFrom),
        dafTo: Number(form.dafTo),
        amudFrom: form.frame === 'shinun' ? form.amudFrom : '',
        amudTo: form.frame === 'shinun' ? form.amudTo : '',
      };
      const body = { title: form.title.trim(), frame: form.frame, description: form.description || '', material };
      if (isEdit) {
        // התאריך נשלח רק כשהשתנה — שינוי שלו מחזיר את המבחן להמתנה / פותח אותו (ומסובב שלוחות)
        const dateChanged = form.opensAt && form.opensAt !== form.opensAtInitial;
        await api.put(`/american-exams/${editingId}`, dateChanged ? { ...body, opensAt: dateInputToIso(form.opensAt) } : body);
        resetDialog();
        toast.success('ההגדרות עודכנו');
        load();
        return;
      }
      const { data: created } = await api.post('/american-exams', { ...body, opensAt: dateInputToIso(form.opensAt), sectionKeys: ['main'] });
      createdId = created._id || created.id;
      const imported = await importFile(createdId, form.file);
      resetDialog();
      toast.success(`המבחן נוצר — ${imported.imported} שאלות`);
      load();
      goToEditor((imported.exam || created)._id || createdId);
    } catch (e) {
      if (createdId) { try { await api.delete(`/american-exams/${createdId}`); } catch { /* rollback best-effort */ } }
      const payload = e.response?.data;
      const detail = Array.isArray(payload?.errors) ? payload.errors.slice(0, 4).join(' · ') : '';
      setFormError(detail ? `${payload?.error || 'שגיאה'}: ${detail}` : (payload?.error || (isEdit ? 'שגיאה בעדכון המבחן' : 'שגיאה ביצירת המבחן')));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (exam) => {
    try {
      await api.put(`/american-exams/${exam._id}`, { status: exam.status === 'active' ? 'inactive' : 'active' });
      load();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בעדכון סטטוס'));
    }
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/american-exams/${deleteTarget._id}`);
      setDeleteTarget(null);
      toast.success('המבחן נמחק');
      load();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה במחיקה'));
    } finally {
      setDeleting(false);
    }
  };
  const openReplace = (exam) => { clearPreview(); setReplaceTarget(exam); setReplaceFile(null); setReplaceError(''); };
  const closeReplace = () => { if (!replacing) { setReplaceTarget(null); setReplaceFile(null); setReplaceError(''); clearPreview(); } };
  const replace = async () => {
    setReplaceError('');
    if (!replaceTarget || !replaceFile) { setReplaceError('יש לבחור קובץ אקסל'); return; }
    if (!excelPreview?.questions?.length) { setReplaceError(previewError || 'יש לבחור קובץ אקסל תקין'); return; }
    setReplacing(true);
    try {
      const imported = await importFile(replaceTarget._id, replaceFile);
      const id = imported.exam?._id || replaceTarget._id;
      toast.success(`הוחלף — ${imported.imported} שאלות`);
      setReplaceTarget(null);
      setReplaceFile(null);
      clearPreview();
      load();
      goToEditor(id);
    } catch (e) {
      const payload = e.response?.data;
      setReplaceError((Array.isArray(payload?.errors) ? payload.errors.slice(0, 3).join(' · ') : '') || payload?.error || 'שגיאה בהחלפת הקובץ');
    } finally {
      setReplacing(false);
    }
  };

  const rows = useMemo(() => {
    const slots = buildIvrSlotMap(exams);
    return exams.map((exam) => ({
      ...exam,
      materialLabel: formatMaterial(exam.material, exam.frame) || '—',
      questionCount: questionTotal(exam),
      countsSummary: formatCountsSummary(exam.questions || []),
      ivrSlot: slots[exam._id] || null,
      createdAtLabel: formatCreatedAt(exam.createdAt),
    }));
  }, [exams]);

  if (!ALLOWED_FRAMES.has(frame)) return <Navigate to="/american-exams/daf_yomi" replace />;
  const frameLabel = FRAME_LABELS[frame] || frame;
  const createDisabled = saving || (!editingId && (!form.file || previewLoading || !!previewError || !excelPreview?.questions?.length));
  const replaceDisabled = replacing || !replaceFile || previewLoading || !!previewError || !excelPreview?.questions?.length;

  const columns = [
    { key: 'title', label: 'כותרת', render: (r) => <b>{r.title}</b> },
    { key: 'materialLabel', label: 'חומר' },
    { key: 'questionCount', label: 'שאלות', type: 'number', filterable: false, render: (r) => <span title={r.countsSummary || ''}>{r.questionCount || '—'}</span> },
    { key: 'status', label: 'סטטוס', type: 'enum', getValue: (r) => statusLabel(r).split(' · ')[0], exportValue: statusLabel, render: (r) => <Tag tone={STATUS_TONES[r.status] || 'neutral'} icon={r.status === 'scheduled' ? 'clock' : undefined}>{statusLabel(r)}</Tag> },
    { key: 'opensAt', label: 'תאריך פתיחה', type: 'date', filterable: false, render: (r) => formatOpensAt(r.opensAt) || '—', exportValue: (r) => formatOpensAt(r.opensAt) },
    { key: 'ivrSlot', label: 'בטלפון', type: 'enum', getValue: (r) => (r.ivrSlot ? IVR_SLOT_LABELS[r.ivrSlot] : '—'), render: (r) => (r.ivrSlot ? <Tag tone={r.ivrSlot === 1 ? 'primary' : 'info'} icon="phone" title={`נוצר: ${r.createdAtLabel}`}>{IVR_SLOT_LABELS[r.ivrSlot]}</Tag> : <span className="ck-muted">—</span>) },
    { key: 'createdAt', label: 'נוצר', type: 'date', filterable: false, render: (r) => r.createdAtLabel, exportValue: (r) => r.createdAtLabel },
  ];

  const fileHint = 'מספר_שאלה · סוג_שאלה · טקסט_שאלה · אפשרות_1–3 · תשובה_נכונה';

  return (
    <>
      <PageHeader
        title={`מבחנים אמריקאים — ${frameLabel}`}
        sub="מסכת + טווח דפים + אקסל. הנבחן עונה לפי הדף המודפס (1–3)."
        primary={{ label: 'מבחן חדש', onClick: openCreate }}
        more={[
          { key: 'tpl', label: downloadingTemplate ? 'מוריד…' : 'הורד תבנית אקסל', icon: 'download', onClick: downloadTemplate, disabled: downloadingTemplate },
          { key: 'refresh', label: 'רענון', icon: 'refresh', onClick: load },
        ]}
      />

      <DataTable
        mode="client"
        rows={rows}
        rowKey="_id"
        columns={columns}
        loading={loading}
        error={error}
        onRetry={load}
        onRefresh={load}
        storageKey={`bchinot-american-exams-${frame}`}
        defaultQuery={{ sort: { key: 'createdAt', dir: -1 } }}
        selectable={false}
        exportFilename={`מבחנים_אמריקאים_${frame}`}
        emptyTitle={`לא נמצאו מבחנים אמריקאים ל«${frameLabel}»`}
        onRowClick={(r) => goToEditor(r._id)}
        actionsWidth={160}
        rowActions={(r) => [
          { key: 'editor', icon: 'edit', label: 'עריכת מבחן', onClick: () => goToEditor(r._id) },
          { key: 'details', icon: 'sliders', label: 'עריכת פרטים', onClick: () => openEdit(r) },
          { key: 'word', icon: 'download', label: 'הורדה כוורד', onClick: () => downloadWord(r) },
        ]}
        rowMenu={(r) => [
          { key: 'editor', label: 'עריכת מבחן', icon: 'edit', onClick: () => goToEditor(r._id) },
          { key: 'details', label: 'עריכת פרטים', icon: 'sliders', onClick: () => openEdit(r) },
          { key: 'word', label: 'הורדה כוורד', icon: 'download', onClick: () => downloadWord(r) },
          { key: 'replace', label: 'החלף אקסל', icon: 'upload', onClick: () => openReplace(r) },
          { key: 'toggle', label: r.status === 'active' ? 'כבה' : 'הפעל', icon: r.status === 'active' ? 'pause' : 'play', onClick: () => toggleActive(r) },
          { divider: true },
          { key: 'del', label: 'מחיקה', icon: 'trash', danger: true, onClick: () => setDeleteTarget(r) },
        ]}
      />

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && resetDialog()}
        title={editingId ? 'עריכת מבחן אמריקאי' : 'מבחן אמריקאי חדש'}
        icon={editingId ? 'edit' : 'plus'}
        size={editingId ? 'md' : 'lg'}
        footer={(
          <>
            <Button variant="ghost" disabled={saving} onClick={resetDialog}>ביטול</Button>
            <Button variant="primary" disabled={createDisabled} onClick={save}>{saving ? (editingId ? 'שומר…' : 'יוצר ומייבא…') : (editingId ? 'שמירה' : 'יצירה וייבוא')}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          <p className="nx-desc">{editingId ? 'עדכון כותרת, סוג, חומר ותיאור. החלפת אקסל נעשית בנפרד.' : 'בחרו טווח חומר, והעלו קובץ אקסל.'}</p>
          <Row><span className="ck-muted">סוג:</span><Tag tone="primary">{FRAME_LABELS[form.frame] || form.frame}</Tag></Row>
          <Field label="כותרת (אופציונלי)"><TextInput value={form.title} placeholder="אם ריק — לפי מסכת ודפים" onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></Field>

          <h3 className="nx-sub-h">טווח חומר</h3>
          <FormGrid cols={2}>
            <Field label="מסכת מ *">
              <ComboBox value={form.tractate ? { value: form.tractate, label: form.tractate } : null} onChange={(o) => setTractateFrom(o?.value || null)} options={TRACTATE_OPTS} getLabel={(o) => o?.label || ''} getKey={(o) => o?.value} placeholder="הקלד לחיפוש מסכת…" />
            </Field>
            <Field label="דף מ *">
              <Row wrap={false}>
                <div style={{ flex: 1 }}><ComboBox value={dafOpt(form.dafFrom)} onChange={(o) => setForm((f) => ({ ...f, dafFrom: o?.value ?? null }))} options={dafFromOpts} getLabel={(o) => o?.label || ''} getKey={(o) => o?.value} placeholder={form.tractate ? 'הקלד לחיפוש דף…' : 'בחרו מסכת קודם'} disabled={!form.tractate} /></div>
                {form.frame === 'shinun' ? <div style={{ width: 130 }}><Select value={form.amudFrom || 'a'} onChange={(v) => setForm((f) => ({ ...f, amudFrom: v }))} options={AMUD_OPTIONS} aria-label="עמוד" /></div> : null}
              </Row>
            </Field>
            <Field label="מסכת עד *">
              <ComboBox value={tractateTo ? { value: tractateTo, label: tractateTo } : null} onChange={(o) => setTractateTo(o?.value || null)} options={TRACTATE_OPTS} getLabel={(o) => o?.label || ''} getKey={(o) => o?.value} placeholder="הקלד לחיפוש מסכת…" />
            </Field>
            <Field label="דף עד *">
              <Row wrap={false}>
                <div style={{ flex: 1 }}><ComboBox value={dafOpt(form.dafTo)} onChange={(o) => setForm((f) => ({ ...f, dafTo: o?.value ?? null }))} options={dafToOpts} getLabel={(o) => o?.label || ''} getKey={(o) => o?.value} placeholder={tractateTo ? 'הקלד לחיפוש דף…' : 'בחרו מסכת קודם'} disabled={!tractateTo} /></div>
                {form.frame === 'shinun' ? <div style={{ width: 130 }}><Select value={form.amudTo || 'b'} onChange={(v) => setForm((f) => ({ ...f, amudTo: v }))} options={AMUD_OPTIONS} aria-label="עמוד" /></div> : null}
              </Row>
            </Field>
          </FormGrid>
          <Field label="תיאור (אופציונלי)"><TextInput value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></Field>

          <Field
            label={editingId && !form.opensAtInitial ? 'תאריך פתיחה' : 'תאריך פתיחה *'}
            hint="היום / תאריך שעבר — המבחן נפתח מיד. תאריך עתידי — «עתידי», ונפתח אוטומטית בתאריך. בפתיחה: הקודם עובר לשלוחה 2 בטלפון, ומה שלפניו נכבה."
          >
            <TextInput type="date" value={form.opensAt || ''} onChange={(e) => setForm((f) => ({ ...f, opensAt: e.target.value }))} />
          </Field>

          {!editingId ? (
            <>
              <h3 className="nx-sub-h">קובץ אקסל</h3>
              <Row>
                <Button size="sm" variant="ghost" icon="download" disabled={downloadingTemplate} onClick={downloadTemplate}>{downloadingTemplate ? 'מוריד…' : 'הורד תבנית'}</Button>
                <small className="ck-muted">{fileHint}</small>
              </Row>
              <FileDrop accept=".xlsx,.xls" icon="file" title={form.file ? form.file.name : 'קובץ אקסל *'} hint={previewLoading ? 'טוען תצוגה מקדימה…' : 'גררו לכאן או לחצו לבחירה'} loading={previewLoading} onFile={(file) => { setForm((f) => ({ ...f, file: file || null })); previewExcel(file); }} />
              <ExcelPreview preview={excelPreview} />
            </>
          ) : null}
          {formError || (!editingId && previewError) ? <Banner tone="danger" icon="alert">{formError || previewError}</Banner> : null}
        </div>
      </Dialog>

      <Dialog
        open={!!replaceTarget}
        onClose={closeReplace}
        title={replaceTarget ? `החלף אקסל · ${replaceTarget.title}` : 'החלף אקסל'}
        icon="upload"
        size="lg"
        footer={(
          <>
            <Button variant="ghost" disabled={replacing} onClick={closeReplace}>ביטול</Button>
            <Button variant="primary" icon="upload" disabled={replaceDisabled} onClick={replace}>{replacing ? 'מייבא…' : 'ייבוא'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          <small className="ck-muted">{fileHint}</small>
          <FileDrop accept=".xlsx,.xls" icon="file" title={replaceFile ? replaceFile.name : 'קובץ אקסל חדש *'} hint={previewLoading ? 'טוען תצוגה מקדימה…' : 'גררו לכאן או לחצו לבחירה'} loading={previewLoading} onFile={(file) => { setReplaceFile(file || null); previewExcel(file); }} />
          {previewLoading && !excelPreview ? <Row><Spinner size={16} /><small className="ck-muted">טוען תצוגה מקדימה…</small></Row> : null}
          <ExcelPreview preview={excelPreview} />
          {replaceError || previewError ? <Banner tone="danger" icon="alert">{replaceError || previewError}</Banner> : null}
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        busy={deleting}
        danger
        title="מחיקת מבחן"
        confirmLabel="מחק"
        message={deleteTarget ? `למחוק את «${deleteTarget.title}»?` : ''}
      />
    </>
  );
}
